import { MODULE_CATALOG, type ModuleKey } from '@alicerce/contracts';
import { Alert, Badge, Button, Card, CardBody, formatBRL } from '@alicerce/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { errorMessage } from '@/lib/api';
import { useApi, useCurrent, useSession } from '@/lib/session';
import { PaymentInstructions, type ChargeView } from './payment-instructions';

/** O que cada módulo resolve, mostrado no lugar de um erro quando ele não está no plano. */
const PITCH: Partial<Record<ModuleKey, string[]>> = {
  storefront: ['Loja com seu domínio e o seu visual', 'Páginas prontas para o Google', 'Botão de WhatsApp e chat'],
  checkout: ['Pix, cartão e boleto', 'Frete calculado na hora', 'Painel de pedidos com etiqueta'],
  inventory: ['Saldo sempre certo, por venda e por entrada', 'Aviso de estoque baixo', 'Inventário pelo celular'],
  finance: ['Quanto entrou, saiu e sobrou no mês', 'Contas a pagar e a receber', 'Calculadora de preço e limite do MEI'],
  fiscal: ['Nota emitida sozinha quando o pedido é pago', 'Rejeições explicadas em português', 'XMLs do mês para o contador'],
  inbox: ['WhatsApp e chat da loja numa caixa só', 'Respostas rápidas e etiquetas', 'Pedidos do cliente ao lado da conversa'],
  ads: ['Campanhas prontas por segmento', 'Revisão da nossa equipe antes de publicar', 'Relatório de vendas por anúncio'],
};

interface Preview {
  added: string[];
  addedDependencies: { moduleKey: string; requiredBy: string; reason: string }[];
  recurringDiffCents: number;
  prorataCents: number;
  quote: { interval: 'MONTHLY' | 'YEARLY' };
}

export function LockedModule({ module }: { module: ModuleKey }) {
  const api = useApi();
  const { refreshMe } = useSession();
  const { can } = useCurrent();
  const queryClient = useQueryClient();
  const def = MODULE_CATALOG[module];
  const [charge, setCharge] = useState<ChargeView | null>(null);
  const preview = useQuery({
    queryKey: ['billing', 'preview', module],
    queryFn: () => api.get<Preview>(`/v1/billing/modules/${module}/preview`),
    enabled: can('billing:read'),
  });
  const activate = useMutation({
    mutationFn: () => api.post<{ status: 'ACTIVE' | 'PENDING_PAYMENT'; charge: ChargeView | null }>(`/v1/billing/modules/${module}`, { method: 'PIX' }),
    onSuccess: async (r) => {
      if (r.status === 'PENDING_PAYMENT' && r.charge) setCharge(r.charge);
      await queryClient.invalidateQueries({ queryKey: ['billing'] });
      await refreshMe();
    },
  });
  const p = preview.data;
  const per = p?.quote.interval === 'YEARLY' ? 'ano' : 'mês';
  const others = p?.added.filter((k) => k !== module) ?? [];

  return (
    <div className="mx-auto max-w-2xl py-6">
      <Card>
        <CardBody className="space-y-5 p-6 sm:p-8">
          <div className="flex items-center gap-3">
            <span className="flex size-10 items-center justify-center rounded-full bg-brand-50 text-brand-700">
              <Lock className="size-5" aria-hidden />
            </span>
            <div>
              <Badge tone="info">Módulo não contratado</Badge>
              <h1 className="mt-1 text-xl font-semibold">{def.name}</h1>
            </div>
          </div>
          <p className="text-muted">{def.description}</p>
          {PITCH[module] && (
            <ul className="list-disc space-y-1 pl-5 text-sm">
              {PITCH[module]!.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          )}
          {charge ? (
            <PaymentInstructions charge={charge} title="Pague para ativar" description="O módulo é liberado assim que o pagamento for confirmado." />
          ) : !can('billing:manage') ? (
            <Alert tone="info">Peça para quem é dono do negócio ativar este módulo em Assinatura.</Alert>
          ) : (
            <div className="space-y-3 rounded-lg border border-line bg-canvas p-4">
              {preview.isPending ? (
                <p className="text-sm text-muted">Calculando o preço…</p>
              ) : p ? (
                <>
                  <p className="text-sm">
                    Sua assinatura passa a custar <strong>{formatBRL(p.recurringDiffCents)}</strong> a mais por {per}.
                    {p.prorataCents > 0 && (
                      <>
                        {' '}
                        Agora você paga só os dias que faltam no ciclo: <strong>{formatBRL(p.prorataCents)}</strong>.
                      </>
                    )}
                    {p.prorataCents === 0 && ' Ativa na hora.'}
                  </p>
                  {others.length > 0 && (
                    <p className="text-sm text-muted">
                      Também entra: {others.map((k) => MODULE_CATALOG[k as ModuleKey]?.name ?? k).join(', ')}.{' '}
                      {p.addedDependencies.map((d) => d.reason).join(' ')}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm text-muted">Veja o preço em Assinatura.</p>
              )}
              {activate.isError && <Alert tone="danger">{errorMessage(activate.error)}</Alert>}
              <div className="flex flex-wrap gap-2">
                <Button loading={activate.isPending} onClick={() => activate.mutate()}>
                  Ativar {def.name}
                </Button>
                <Link to="/assinatura" className="inline-flex h-10 items-center px-3 text-sm text-brand-700 hover:underline">
                  Ver meu plano
                </Link>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
